import * as React from 'react';
import { History } from 'history';
import styled from 'styled-components';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import { useHistory, useLocation } from 'react-router-dom';

import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import DashboardLayout from '../../layouts/DashboardLayout';
import {
  createMessage,
  getMessagesList,
  updateMessage,
  deleteMessage,
  MessagesButtonTypeLink,
  MessagesTableType,
  reUseMessage,
} from '../../api/messages';
import SubMenuHeadLine from '../../organisms/SubMenuHeadLine';
import Table from '../../organisms/Table';
import ActionsPanel from '../../organisms/ActionsPanel';
import useTablePage from '../../hooks/useTablePage';
import { convertTableUTCDate } from '../../utils/convertTableValues';
import { ModalDefaultDraggable } from '../../molecules';
import { ROUTES } from '../../constants/routes';
import ModalInfo from '../../molecules/ModalInfo';
import MessagesForm from '../../forms/MessagesForm';
import { htmlToPlainText } from '../../utils/sanitizeHtml';
import RichMessageText from '../../molecules/RichMessageText';
import convertObjToRouteObj from '../../utils/convertObjToRouteObj';
import {
  SUPER_ADMIN_ROLE,
  USER_TYPES,
  USER_TYPES_VALUES,
} from '../../constants/forms';
import isRoutePathExact from '../../utils/isRoutePathExact';

const messagesRoutes = USER_TYPES.reduce((acc, current) => {
  if (
    current.value === USER_TYPES_VALUES.FRANCHISE_ADMIN ||
    current.value === SUPER_ADMIN_ROLE
  ) {
    return acc;
  }

  return [
    ...acc,
    { id: current.value, title: current.label },
  ];
}, []);

const MessagesPage = () => {
  const {
    search,
    setSearch,
    modalError,
    modalSuccess,
    isModalOpen,
    modalTitle,
    values,
    modalErrorTitle,
    errors,
    setErrors,
    tableManuallyUpdate,
    createOrUpdate,
    handleAdd,
    handleCloseModal,
    handleCloseModalInfo,
  } = useTablePage<MessagesTableType>({
    createEndpoint: createMessage,
    updateEndpoint: reUseMessage,
    modalCreateTitle: 'Add Message',
    modalUpdateTitle: 'Update Message',
  });
  const { pathname } = useLocation();
  const { push }: History = useHistory();

  const [routes, setRoutes] = React.useState<
    MessagesButtonTypeLink[]
  >(null);

  const [userRole, setUserRole] = React.useState<
    MessagesButtonTypeLink
  >(null);

  React.useEffect(() => {
    setRoutes(convertObjToRouteObj(messagesRoutes) as any);
  }, []);

  React.useEffect(() => {
    if (routes && pathname === ROUTES.SETTINGS_MESSAGES) {
      setUserRole(routes[0]);
      push(`${pathname}/${routes[0].linkTo}`);
      return;
    }

    if (
      routes &&
      isRoutePathExact(pathname, ROUTES.SETTINGS_MESSAGES)
    ) {
      const pathArr = pathname.split('/');
      const searchPathObj = routes.find(
        el => el.linkTo === pathArr[pathArr.length - 1],
      );

      setUserRole(searchPathObj);

      return;
    }

    setUserRole(null);
  }, [routes, pathname]);

  // The body keeps its formatting in the table: colour, bold and alignment are
  // the point of the rich-text editor, so flattening them here hid the very
  // thing the author just set.
  const formatMessageValue = (value: any, column: string) => {
    if (column === 'text') {
      return <RichMessageText html={value} />;
    }

    if (column === 'dateFrom' || column === 'dateTo') {
      if (!value) return '—';
      // DRF's global DATE_FORMAT is '%m/%d/%Y' (dashboard_project/settings.py),
      // so these already arrive as MM/DD/YYYY - pass them straight through.
      // The ISO branch is a fallback in case that setting ever changes; parsing
      // either through Date() would shift the day west of Greenwich.
      const text = String(value);
      const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return iso ? `${iso[2]}/${iso[3]}/${iso[1]}` : text;
    }

    return convertTableUTCDate(value, column);
  };

  const columns = column => {
    if (
      [
        'id',
        'isDeleted',
        'isActive',
        'franchise',
        'franchiseNames',
        'userRole',
        'addresses',
        // Now serialized so the dashboard block can credit the author. Hidden
        // here on purpose: this table lists messages the admin sent, so a
        // Sender column would repeat their own name on every row.
        'sender',
      ].includes(column)
    ) {
      return null;
    }

    if (column === 'publicationDate') {
      return 'Date';
    }

    if (column === 'dateFrom') {
      return 'Show From';
    }

    if (column === 'dateTo') {
      return 'Show Until';
    }

    return tableColumnsFormatter(column);
  };

  const [editRow, setEditRow] = React.useState<any>(null);
  const [deleteRow, setDeleteRow] = React.useState<any>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [rowError, setRowError] = React.useState('');
  // useTablePage owns its own counter; this one refreshes after row actions.
  const [rowRefresh, setRowRefresh] = React.useState(0);

  // What the audience was, for the read-only line in the edit modal.
  const audienceLabelFor = (row: any) => {
    const addresses = (row?.addressesList || '').trim();
    const branches = (row?.franchiseNames || '').trim();
    if (addresses && addresses !== 'All') return addresses;
    const everyone = `All ${userRole?.title || 'recipient'}s`;
    return branches ? `${everyone} · ${branches}` : everyone;
  };

  const handleEditSubmit = (fields: any) => {
    setIsBusy(true);
    return updateMessage(fields.id, {
      text: fields.text,
      dateFrom: fields.dateFrom,
      dateTo: fields.dateTo,
    })
      .then(() => {
        setEditRow(null);
        setRowRefresh(value => value + 1);
      })
      .catch(error => {
        const data = error?.response?.data;
        const message = data?.text || data?.detail ||
          'Could not save the message.';
        setRowError(
          Array.isArray(message) ? message[0] : message,
        );
      })
      .finally(() => setIsBusy(false));
  };

  const handleDeleteConfirm = () => {
    setIsBusy(true);
    deleteMessage(deleteRow.id)
      .then(() => {
        setDeleteRow(null);
        setRowRefresh(value => value + 1);
      })
      .catch(error => {
        const data = error?.response?.data;
        setRowError(
          data?.detail || 'Could not delete the message.',
        );
      })
      .finally(() => setIsBusy(false));
  };

  let isReuseFirstRequest = true;

  const additionalActions = () => [
    {
      title: 'Reuse',
      handler: fields => {
        if (isReuseFirstRequest) {
          createOrUpdate(fields).finally(() => {
            isReuseFirstRequest = true;
            setErrors(null);
          });

          isReuseFirstRequest = false;
        }
      },
      Icon: RefreshIcon,
    },
    {
      title: 'Edit',
      handler: row => setEditRow(row),
      Icon: EditIcon,
    },
    {
      title: 'Delete',
      handler: row => setDeleteRow(row),
      Icon: DeleteIcon,
    },
  ];

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  return (
    <DashboardLayout>
      <ActionsPanel
        isActionPanelMoreVisible={false}
        onSearch={setSearch}
        onAdd={handleAdd}
      />

      <StyledSubMenuHeadLine
        menuLinks={routes as any}
        basePath={ROUTES.SETTINGS_MESSAGES}
      />

      {userRole && (
        <Container>
          <Table
            customSearch
            searchQuery={search}
            columns={columns}
            rows={getMessagesList}
            formatValue={formatMessageValue}
            manuallyUpdate={tableManuallyUpdate + rowRefresh}
            additionalRequestParams={{
              userRole: userRole.id,
            }}
            additionalActions={additionalActions}
          />
        </Container>
      )}

      {editRow && (
        <ModalDefaultDraggable
          onClose={() => {
            if (!isBusy) setEditRow(null);
          }}
          title="Edit Message"
        >
          <MessagesForm
            isEditing
            audienceLabel={audienceLabelFor(editRow)}
            initialValues={{
              id: editRow.id,
              text: editRow.text,
              dateFrom: editRow.dateFrom,
              dateTo: editRow.dateTo,
            }}
            modalError={false}
            onSubmit={handleEditSubmit}
            selectedUserRole={userRole.id}
            updateButtonText="Save"
          />
        </ModalDefaultDraggable>
      )}

      {deleteRow && (
        <ModalDefaultDraggable
          narrow
          centered
          onClose={() => {
            if (!isBusy) setDeleteRow(null);
          }}
          title="Delete message"
        >
          <ConfirmBody>
            <ConfirmText>
              This message will be removed permanently.
              There is no undo.
            </ConfirmText>
            <ConfirmQuote>
              {htmlToPlainText(deleteRow.text).slice(
                0,
                180,
              ) || '(empty message)'}
              {htmlToPlainText(deleteRow.text).length > 180 &&
                '…'}
            </ConfirmQuote>
            <ConfirmActions>
              <CancelButton
                type="button"
                disabled={isBusy}
                onClick={() => setDeleteRow(null)}
              >
                Cancel
              </CancelButton>
              <DangerButton
                type="button"
                disabled={isBusy}
                onClick={handleDeleteConfirm}
              >
                {isBusy ? 'Deleting…' : 'Delete'}
              </DangerButton>
            </ConfirmActions>
          </ConfirmBody>
        </ModalDefaultDraggable>
      )}

      {rowError.length > 0 && (
        <ModalInfo
          timeToClose={5000}
          isError
          errorMsg={rowError}
          onClose={() => setRowError('')}
        />
      )}

      {isModalOpen && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
        >
          <MessagesForm
            initialValues={values}
            initialErrors={errors}
            modalError={modalErrorTitle.length > 0}
            onSubmit={createOrUpdate}
            selectedUserRole={userRole.id}
          />
        </ModalDefaultDraggable>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={5000}
          isError={modalError}
          onClose={handleCloseModalInfo}
          errorMsg={modalErrorTitle}
        />
      )}
    </DashboardLayout>
  );
};

const Container = styled.div``;

const ConfirmBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: var(--mainText);
`;

const ConfirmQuote = styled.blockquote`
  margin: 0;
  padding: 12px 14px;
  border-left: 3px solid var(--border);
  background: var(--neutral-50);
  border-radius: 0 8px 8px 0;
  font-size: 0.875rem;
  color: var(--neutral-700);
  overflow-wrap: anywhere;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
`;

const CancelButton = styled.button`
  min-width: 110px;
  height: 40px;
  padding: 0 18px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:enabled {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const DangerButton = styled.button`
  min-width: 110px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--danger-500);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:enabled {
    background: var(--danger-600);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus-danger);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const StyledSubMenuHeadLine = styled(SubMenuHeadLine)`
  padding: 16px 0;
  zoom: 0.9;
`;

export default MessagesPage;
